import { TIME_GEOMETRY_SELECTOR, gridHeaderPx } from "./calendar-drag-geometry";

/**
 * Cursor-anchored zoom: keep whatever the pointer is over exactly where it is
 * while the grid resizes underneath it.
 *
 * MEASURED, never computed. Two things defeat an arithmetic model:
 *   • the fine rows are `minmax(Xrem, 1fr)` and stretch past their rem minimum
 *     whenever the visible window is shorter than the pane — getCalendarTimeAxis
 *     documents its own rem rate as a FLOOR, so a px-per-minute formula
 *     understates the real scale and the anchor slides away from the cursor;
 *   • the chrome around the body (all-day strip, day-name header, hour gutter,
 *     scrollbar gutter) is fixed or content-sized, so scaling the whole scroll
 *     extent drags the anchor by that chrome.
 *
 * So the anchor is expressed in the grid's own terms — a fraction down the time
 * body and a fractional column across — captured from real rects before the
 * zoom and resolved against real rects after it.
 */

/** Position of a pointer inside a grid, in grid-relative terms. */
export function anchorFromRects({ gridRect, columnRect, headerPx, clientX, clientY }) {
  const bodyHeight = gridRect ? gridRect.height - headerPx : 0;
  const fractionY = bodyHeight > 0 ? (clientY - gridRect.top - headerPx) / bodyHeight : null;
  const column =
    columnRect && columnRect.width > 0 ? (clientX - columnRect.left) / columnRect.width : null;
  if (fractionY == null && column == null) return null;
  return { clientX, clientY, fractionY, column };
}

/** Where that same point sits now, given freshly measured rects. */
export function anchorTargets({ gridRect, columnRect, headerPx, anchor }) {
  const bodyHeight = gridRect ? gridRect.height - headerPx : 0;
  return {
    clientY:
      anchor?.fractionY != null && bodyHeight > 0
        ? gridRect.top + headerPx + anchor.fractionY * bodyHeight
        : null,
    clientX:
      anchor?.column != null && columnRect && columnRect.width > 0
        ? columnRect.left + anchor.column * columnRect.width
        : null,
  };
}

function rects(viewport) {
  // In the week view the columns live in a background layer and the event grid
  // carries `data-time-grid`; in the day view one element is both. Either way
  // the FIRST match is a full-height column whose width is one day/tenant.
  const columnEl = viewport?.querySelector("[data-time-col]") || null;
  const gridEl = viewport?.querySelector(TIME_GEOMETRY_SELECTOR) || columnEl;
  const gridRect = gridEl?.getBoundingClientRect() || null;
  return {
    gridRect: gridRect && gridRect.height > 0 ? gridRect : null,
    columnRect: columnEl?.getBoundingClientRect() || null,
  };
}

/** Read the pointer's grid-relative position, before the zoom relayout. */
export function captureZoomAnchor(viewport, clientX, clientY) {
  if (!viewport) return null;
  return anchorFromRects({ ...rects(viewport), headerPx: gridHeaderPx(), clientX, clientY });
}

/**
 * The viewport's own centre, captured exactly as a cursor anchor is — used when
 * the zoom came from the Display popover rather than the wheel, so both paths
 * restore through the same measured code.
 */
export function centreZoomAnchor(viewport) {
  if (!viewport) return null;
  const rect = viewport.getBoundingClientRect();
  return captureZoomAnchor(
    viewport,
    rect.left + viewport.clientWidth / 2,
    rect.top + viewport.clientHeight / 2,
  );
}

/**
 * Scroll the viewport so the captured point is back under the pointer. Call
 * AFTER the relayout has committed. Returns which axes it was able to restore,
 * so callers can fall back to their centre-anchored math for the rest.
 */
export function applyZoomAnchor(viewport, anchor, { vertical = false, horizontal = false } = {}) {
  const restored = { vertical: false, horizontal: false };
  if (!viewport || !anchor) return restored;
  const targets = anchorTargets({ ...rects(viewport), headerPx: gridHeaderPx(), anchor });
  if (vertical && targets.clientY != null) {
    const max = Math.max(viewport.scrollHeight - viewport.clientHeight, 0);
    const next = viewport.scrollTop + (targets.clientY - anchor.clientY);
    viewport.scrollTop = Math.min(Math.max(next, 0), max);
    restored.vertical = true;
  }
  if (horizontal && targets.clientX != null) {
    const max = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    const next = viewport.scrollLeft + (targets.clientX - anchor.clientX);
    viewport.scrollLeft = Math.min(Math.max(next, 0), max);
    restored.horizontal = true;
  }
  return restored;
}
